// /portal/settings — per-user email notification toggles.
//
// First resident of Settings. All roles see it (route is open, nav item
// is unconditional). Reads + writes the caller's own portal_users.
// notification_prefs JSONB column.
//
// Opt-out model (mirrors the column COMMENT and the carrier-side check):
// ABSENT KEY = TRUE — '{}' means "all on". Only an explicit `false`
// mutes a category. Toggles default to ON when the key is absent so the
// UI matches the actual send-time behavior.
//
// RLS / column-level safety: a self_update policy on portal_users
// (auth_user_id = auth.uid()) gates the row; portal_users_self_update_guard
// (BEFORE UPDATE trigger) restricts what columns may change — only
// notification_prefs (and last_login_at, for the existing login touch).
// A portal user attempting to mutate role / company_id / etc. through
// this page would hit a check_violation, not a silent success.

import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { toast } from 'sonner'

type EventSlug = 'job_scheduled' | 'job_completed' | 'request_converted' | 'quote_sent' | 'vendor_en_route'

interface ToggleRow {
  slug: EventSlug
  label: string
  description: string
}

const TOGGLES: ToggleRow[] = [
  { slug: 'job_scheduled',     label: 'Job scheduled',   description: 'When a visit is scheduled for your company' },
  { slug: 'job_completed',     label: 'Job completed',   description: 'When work at your site is finished' },
  { slug: 'request_converted', label: 'Request updates', description: 'When your service request becomes a scheduled job' },
  { slug: 'quote_sent',        label: 'New quotes',      description: 'When a quote is sent to you' },
  { slug: 'vendor_en_route',   label: 'Technician on the way', description: 'Email me when a technician heads to my job.' },
]

type Prefs = Partial<Record<EventSlug, boolean>>

export default function PortalSettings() {
  const { portalUser } = useAuth()
  const queryClient = useQueryClient()

  const prefsQueryKey = useMemo(() => ['portal-self-prefs', portalUser?.id], [portalUser?.id])

  const { data: prefs = {} as Prefs, isLoading } = useQuery({
    queryKey: prefsQueryKey,
    enabled: !!portalUser?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portal_users')
        .select('notification_prefs')
        .eq('id', portalUser!.id)
        .maybeSingle()
      if (error) throw error
      return (data?.notification_prefs ?? {}) as Prefs
    },
  })

  // Optimistic flip with rollback-on-error. The opt-out shape means the
  // displayed state for an absent key is ON, so toggling OFF writes
  // `{ ...prev, [slug]: false }`, and toggling ON writes the opposite.
  // We persist explicit booleans both ways rather than deleting keys —
  // makes the per-user audit trail easier to reason about.
  const toggleMutation = useMutation({
    mutationFn: async ({ next }: { slug: EventSlug; next: Prefs }) => {
      const { error } = await supabase
        .from('portal_users')
        .update({ notification_prefs: next })
        .eq('id', portalUser!.id)
      if (error) throw error
    },
    onMutate: async ({ slug, next }) => {
      await queryClient.cancelQueries({ queryKey: prefsQueryKey })
      const previous = queryClient.getQueryData<Prefs>(prefsQueryKey)
      queryClient.setQueryData<Prefs>(prefsQueryKey, next)
      return { previous, slug }
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous !== undefined) queryClient.setQueryData(prefsQueryKey, ctx.previous)
      toast.error(`Couldn't save preference — ${err instanceof Error ? err.message : 'unknown error'}`)
    },
    onSettled: () => { queryClient.invalidateQueries({ queryKey: prefsQueryKey }) },
  })

  // The display contract: absent OR true → toggle is ON; only `false`
  // shows as OFF.
  const isOn = (slug: EventSlug): boolean => prefs[slug] !== false

  const onToggle = (slug: EventSlug) => {
    const currentlyOn = isOn(slug)
    const next: Prefs = { ...prefs, [slug]: !currentlyOn }
    toggleMutation.mutate({ slug, next })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>Settings</h1>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-tertiary)' }}>Manage what you'd like to be emailed about.</p>
      </header>

      <section style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email notifications</h2>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--ink-tertiary)' }}>Pick which emails you'd like to receive. Changes save automatically.</p>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {TOGGLES.map((t, idx) => {
            const on = isOn(t.slug)
            return (
              <div key={t.slug}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderTop: idx === 0 ? 'none' : '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <label htmlFor={`pref-${t.slug}`}
                    style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-primary)', cursor: isLoading ? 'default' : 'pointer' }}>
                    {t.label}
                  </label>
                  <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginTop: 2 }}>{t.description}</span>
                </div>
                <button
                  id={`pref-${t.slug}`}
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={`${t.label} — ${on ? 'on' : 'off'}`}
                  disabled={isLoading || toggleMutation.isPending}
                  onClick={() => onToggle(t.slug)}
                  style={{
                    position: 'relative', flexShrink: 0,
                    width: 40, height: 22, borderRadius: 999, padding: 0,
                    border: `1px solid ${on ? 'var(--accent-border)' : 'var(--border-default)'}`,
                    background: on ? 'var(--accent)' : 'var(--hover-bg)',
                    cursor: isLoading ? 'default' : 'pointer',
                    transition: 'background 120ms ease, border-color 120ms ease',
                  }}>
                  <span aria-hidden="true"
                    style={{
                      position: 'absolute', top: 2, left: on ? 20 : 2,
                      width: 16, height: 16, borderRadius: '50%',
                      background: on ? 'var(--accent-ink)' : 'var(--surface)',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                      transition: 'left 120ms ease, background 120ms ease',
                    }} />
                </button>
              </div>
            )
          })}
        </div>

        <p style={{ margin: '18px 0 0', fontSize: 12, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>
          Account and security emails can't be turned off.
        </p>
      </section>
    </div>
  )
}
